/**
 * Auth utilities — cookie-based.
 *
 * The server sets a HttpOnly cookie on login; JavaScript cannot read it.
 * The browser sends it automatically with every request that uses
 * `credentials: "include"`.  User identity is fetched via GET /api/user-info.
 */
import { getBaseUrl } from "../redux/config";

// ---------------------------------------------------------------------------
// Fetch wrapper — ensures HttpOnly cookie is sent on every request
// ---------------------------------------------------------------------------

/**
 * Drop-in replacement for window.fetch that always sends the session cookie.
 * All API calls should use this instead of bare `fetch()`.
 */
export function secureFetch(url, options = {}) {
  const credentials = usesDevAuthStorage() ? "omit" : "include";
  return fetch(url, { ...options, credentials });
}

/** In dev-auth mode skip cross-site cookies so a stale Okta cookie cannot override Bearer. */
export function shouldSendAuthCookies() {
  return !usesDevAuthStorage();
}

// ---------------------------------------------------------------------------
// /api/user-info — server-side identity
// ---------------------------------------------------------------------------

let _cachedMe = null;
let _cachedIncomingName = null;

/**
 * Call GET /api/user-info to read the authenticated user from the HttpOnly cookie.
 * Returns { email, name, preferred_username, business_function, has_access } or null on 401.
 */
export async function fetchCurrentUser(force = false) {
  if (_cachedMe && !force) return _cachedMe;
  try {
    const res = await secureFetch(`${getBaseUrl()}/api/user-info`);
    if (!res.ok) return null;
    _cachedMe = await res.json();
    return _cachedMe;
  } catch {
    return null;
  }
}

/** True when local dev auth uses sessionStorage (not deployed cookie auth). */
export function usesDevAuthStorage() {
  // Both checks required — production builds bake NODE_ENV="production" at compile
  // time, so this is always false in deployed artifacts even if REACT_APP_DEV_AUTH
  // were accidentally set in a pipeline.
  return (
    process.env.NODE_ENV === "development" &&
    process.env.REACT_APP_DEV_AUTH === "true"
  );
}

function getDevAuthItem(key) {
  if (!usesDevAuthStorage()) return "";
  return sessionStorage.getItem(key) || "";
}

export function getCachedUser() {
  return _cachedMe;
}

export function clearCachedUser() {
  _cachedMe = null;
}

/** Display name for the logged-in (incoming) user — memory in prod, sessionStorage in dev. */
export function getIncomingDisplayName() {
  if (_cachedIncomingName) return _cachedIncomingName;
  const user = getCachedUser();
  if (user?.name) return String(user.name).trim();
  return usesDevAuthStorage() ? getDevAuthItem("auth_incoming_name") : "";
}

export function setIncomingDisplayName(name) {
  const trimmed = String(name || "").trim();
  if (!trimmed) return;
  _cachedIncomingName = trimmed;
  if (usesDevAuthStorage()) {
    sessionStorage.setItem("auth_incoming_name", trimmed);
  }
}

export function clearIncomingDisplayName() {
  _cachedIncomingName = null;
}

// ---------------------------------------------------------------------------
// Silent token refresh — keeps session alive
// ---------------------------------------------------------------------------

let _refreshInterval = null;

/**
 * POST /api/refresh-token to get a fresh JWT cookie.
 * Returns true on success, false if the session is dead (user must re-login).
 */
export async function refreshSession() {
  try {
    const res = await secureFetch(`${getBaseUrl()}/api/refresh-token`, {
      method: "POST",
    });
    if (res.ok) return true;
    if (res.status === 401) {
      stopSessionRefresh();
      window.location.href = `${getBaseUrl()}/api/okta/login`;
      return false;
    }
    return false;
  } catch {
    return false;
  }
}

const REFRESH_INTERVAL_MS = 30 * 60 * 1000; // every 30 minutes

/**
 * Start a background interval that silently refreshes the session cookie.
 * Call once after login / page load.
 */
export function startSessionRefresh() {
  if (_refreshInterval) return;
  _refreshInterval = setInterval(refreshSession, REFRESH_INTERVAL_MS);
}

/**
 * Stop the background refresh (e.g. on logout).
 */
export function stopSessionRefresh() {
  if (_refreshInterval) {
    clearInterval(_refreshInterval);
    _refreshInterval = null;
  }
}

// ---------------------------------------------------------------------------
// Legacy helpers kept for backward compat (no-ops or thin wrappers)
// ---------------------------------------------------------------------------

export const AUTH_SESSION_STORAGE_KEYS = [
  "auth_user_email",
  "auth_outgoing_email",
  "auth_incoming_email",
  "auth_incoming_name",
  "auth_business_function",
  "auth_token",
];

/** True when the Okta login identity differs from the data/outgoing identity. */
export function isDelegatedSession() {
  const outgoing = getOutgoingEmailFromToken();
  const incoming = getIncomingEmailFromToken();
  const out = outgoing.trim().toLowerCase();
  const inc = incoming.trim().toLowerCase();
  return Boolean(out && inc && out !== inc);
}

/** Outgoing/data identity (source of truth for API user-email headers). */
export function getOutgoingEmailFromToken() {
  const cached = getCachedUser();
  if (cached?.email) {
    return String(cached.email).trim();
  }

  if (!usesDevAuthStorage()) return "";

  const payload = decodeJwtPayload(getDevAuthItem("auth_token"));
  return (
    (payload?.resource_outgoing_email || payload?.user_email || "")
      .toString()
      .trim() ||
    getDevAuthItem("auth_outgoing_email") ||
    getDevAuthItem("auth_user_email") ||
    ""
  );
}

/** Logged-in (incoming) identity; used for chat segregation only. */
export function getIncomingEmailFromToken() {
  const cached = getCachedUser();
  const outgoing = getOutgoingEmailFromToken();
  if (cached) {
    return (
      String(cached.preferred_username || cached.email || outgoing).trim() ||
      outgoing
    );
  }

  if (!usesDevAuthStorage()) return outgoing;

  const payload = decodeJwtPayload(getDevAuthItem("auth_token"));
  return (
    (
      payload?.resource_incoming_email ||
      payload?.incoming_user_email ||
      payload?.preferred_username ||
      ""
    )
      .toString()
      .trim() ||
    getDevAuthItem("auth_incoming_email") ||
    outgoing
  );
}

function decodeJwtPayload(token) {
  try {
    const parts = String(token || "").split(".");
    if (parts.length < 2) return null;
    const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const pad = payload + "=".repeat((4 - (payload.length % 4)) % 4);
    return JSON.parse(window.atob(pad));
  } catch {
    return null;
  }
}

export function getAuthApiHeaders(options = {}) {
  const {
    includeUserEmail = true,
    includeBusinessFunction = true,
    includeIncomingEmail = false,
  } = options;
  const headers = {};
  const cached = getCachedUser();
  const token = usesDevAuthStorage() ? getDevAuthItem("auth_token") : "";
  const outgoingEmail = getOutgoingEmailFromToken();

  if (token) headers.Authorization = `Bearer ${token}`;
  if (includeIncomingEmail) {
    const incomingEmail = getIncomingEmailFromToken();
    if (incomingEmail) headers["x-incoming-email"] = incomingEmail;
  }
  if (includeUserEmail && outgoingEmail) {
    headers["user-email"] = outgoingEmail;
    headers["x-user-email"] = outgoingEmail;
  }
  if (includeBusinessFunction) {
    const business =
      cached?.business_function || getDevAuthItem("auth_business_function");
    if (business) headers["business-function"] = business;
  }
  return headers;
}

export function getClientAuth() {
  const user = getCachedUser();
  const outgoingEmail = getOutgoingEmailFromToken();
  const incomingEmail = getIncomingEmailFromToken();
  return {
    userEmail: outgoingEmail,
    outgoingEmail,
    incomingEmail,
    businessFunction: user?.business_function || getDevAuthItem("auth_business_function"),
    token: usesDevAuthStorage() ? getDevAuthItem("auth_token") : "",
    client: "",
  };
}

const DEV_AUTH_URL_PARAMS = [
  "user_email",
  "auth_user_email",
  "outgoing_user_email",
  "incoming_user_email",
  "auth_outgoing_email",
  "auth_incoming_email",
  "auth_incoming_name",
  "incoming_display_name",
  "business_function",
  "auth_business_function",
  "role",
  "token",
  "auth_token",
];

/**
 * Local dev only: read auth values from URL query params into sessionStorage,
 * then strip them from the address bar so they are not bookmarked or shared.
 *
 * Example:
 *   http://localhost:3000/?user_email=you@example.com&business_function=SALESREP&auth_incoming_name=Jane%20Manager
 *
 * Requires NODE_ENV=development and REACT_APP_DEV_AUTH=true (.env.local).
 */
export function bootstrapAuthFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const hasAuthParams = DEV_AUTH_URL_PARAMS.some((key) => params.has(key));

  if (
    process.env.NODE_ENV === "development" &&
    process.env.REACT_APP_DEV_AUTH === "true"
  ) {
    const email = params.get("user_email") || params.get("auth_user_email");
    const outgoingParam =
      params.get("outgoing_user_email") || params.get("auth_outgoing_email");
    const incomingParam =
      params.get("incoming_user_email") || params.get("auth_incoming_email");
    const role =
      params.get("business_function") ||
      params.get("role") ||
      params.get("auth_business_function");
    const token = params.get("token") || params.get("auth_token");
    const payload = decodeJwtPayload(token || "");
    const outgoingEmail =
      (payload?.resource_outgoing_email ||
        payload?.user_email ||
        outgoingParam ||
        email ||
        "")
        .toString()
        .trim();
    const incomingEmail =
      (payload?.resource_incoming_email ||
        payload?.incoming_user_email ||
        payload?.preferred_username ||
        incomingParam ||
        outgoingEmail)
        .toString()
        .trim();
    const incomingDisplayName =
      (
        params.get("auth_incoming_name") ||
        params.get("incoming_display_name") ||
        payload?.incoming_display_name ||
        payload?.name ||
        ""
      )
        .toString()
        .trim();

    if (outgoingEmail || email)
      sessionStorage.setItem("auth_user_email", outgoingEmail || email);
    if (outgoingEmail)
      sessionStorage.setItem("auth_outgoing_email", outgoingEmail);
    if (incomingEmail)
      sessionStorage.setItem("auth_incoming_email", incomingEmail);
    if (incomingDisplayName)
      sessionStorage.setItem("auth_incoming_name", incomingDisplayName);
    if (role) sessionStorage.setItem("auth_business_function", role);
    if (token) sessionStorage.setItem("auth_token", token);

    seedDevAuthFromEnv();
  }

  if (hasAuthParams) {
    const cleanParams = new URLSearchParams();
    if (params.get("tab")) cleanParams.set("tab", params.get("tab"));
    if (params.get("source")) cleanParams.set("source", params.get("source"));
    const qs = cleanParams.toString();
    const cleanUrl =
      window.location.origin +
      window.location.pathname +
      (qs ? `?${qs}` : "") +
      window.location.hash;
    window.history.replaceState({}, document.title, cleanUrl);
  }

  return null;
}

/** Seed sessionStorage from REACT_APP_DEV_* when keys are missing. */
export function seedDevAuthFromEnv() {
  if (!usesDevAuthStorage()) return;

  const envEmail = String(process.env.REACT_APP_DEV_EMAIL || "").trim();
  const envRole = String(process.env.REACT_APP_DEV_ROLE || "").trim();
  const email =
    sessionStorage.getItem("auth_user_email") ||
    sessionStorage.getItem("auth_outgoing_email") ||
    envEmail;
  const role = sessionStorage.getItem("auth_business_function") || envRole;
  const incoming = sessionStorage.getItem("auth_incoming_email") || email;

  if (email) {
    sessionStorage.setItem("auth_user_email", email);
    sessionStorage.setItem("auth_outgoing_email", email);
  }
  if (incoming) sessionStorage.setItem("auth_incoming_email", incoming);
  if (role) sessionStorage.setItem("auth_business_function", role);
}

export function hasClientAuth() {
  return _cachedMe !== null;
}

export function clearClientAuth() {
  clearCachedUser();
  clearIncomingDisplayName();
  if (usesDevAuthStorage()) {
    AUTH_SESSION_STORAGE_KEYS.forEach((k) => sessionStorage.removeItem(k));
  }
}

// ---------------------------------------------------------------------------
// Query-param helper (still useful for ?tab=, ?source=)
// ---------------------------------------------------------------------------

export function getQueryParam(searchString, name, defaultValue = null) {
  const value = new URLSearchParams(searchString).get(name);
  return value === null ? defaultValue : value;
}
